import { ChartItem } from '@common/components/charts';
import { AnyObject } from '@common/types/queryState';
import { Color } from '@common/types/radix';
import { MetricDataPoint } from '@features/Collection';

export interface ChartData {
    name: string;
    color: Color;
}
export interface RawAssetData {
    name: string;
    count: number;
    size: number;
}
export interface RawBarData {
    name: string;
    value: number;
}

const CHART_COLORS: Color[] = [
    'amber',
    'blue',
    'grass',
    'tomato',
    'violet',
    'orange',
    'pink',
    'cyan',
    'plum',
    'teal',
];

export const formatLabel = (label: string): string => label.replace(/_/g, ' ');

const createLookup = (data: MetricDataPoint[]): Record<string, number> =>
    data.reduce(
        (acc, item) => ({ ...acc, [item.name]: item.value }),
        {} as Record<string, number>,
    );

export const mergeCategoryMetrics = (
    volumes: MetricDataPoint[],
    counts: MetricDataPoint[],
): RawAssetData[] => {
    const volumeLookup = createLookup(volumes);
    const countLookup = createLookup(counts);

    const uniqueNames = Array.from(
        new Set([...Object.keys(volumeLookup), ...Object.keys(countLookup)]),
    );

    return uniqueNames.map((rawName) => ({
        name: formatLabel(rawName),
        size: volumeLookup[rawName] ?? 0,
        count: countLookup[rawName] ?? 0,
    }));
};

export const groupData = <T extends { name: string }>(
    data: T[],
    limit: number,
    sortFn: (a: T, b: T) => number,
    createOther: (tail: T[], otherLabel: string) => T,
    otherLabel: string,
): T[] => {
    const sorted = [...data].sort(sortFn);

    if (sorted.length <= limit) return sorted;

    const top = sorted.slice(0, limit);
    const tail = sorted.slice(limit);

    return [...top, createOther(tail, otherLabel)];
};

export const applyChartFormatting = <T extends { name: string }>(
    data: T[],
    otherLabel: string,
): (T & ChartItem)[] => {
    return data.map((item, index) => {
        const color =
            item.name === otherLabel
                ? 'slate'
                : CHART_COLORS[index % CHART_COLORS.length];
        return {
            ...item,
            color,
            fill: `var(--${color}-9)`,
        } as T & ChartItem;
    });
};

export const withVisualScaling = <T extends AnyObject, K extends keyof T>(
    data: T[],
    valueKey: K,
): (T & { visualHeight: number })[] => {
    const values = data.map((item) => {
        const val = item[valueKey];
        return typeof val === 'number' ? val : Number(val) || 0;
    });

    const max = Math.max(...values);
    const nonZero = values.filter((v) => v > 0);

    if (nonZero.length === 0) {
        return data.map((item) => ({ ...item, visualHeight: 0 }));
    }

    const min = Math.min(...nonZero);
    const ratio = max / min;

    const exponent = ratio > 1000 ? 0.25 : ratio > 50 ? 0.5 : 1;

    return data.map((item, index) => ({
        ...item,
        visualHeight: Math.pow(values[index], exponent),
    }));
};
